import React, { useState, useEffect, useMemo } from 'react';
import { Avatar, SkeletonLoading } from '@saleshandy/design-system';
import { ArrowsDiagonal } from '@saleshandy/icons';
import { accessibleOnClick } from '../../../../../shared/utils/accessible-on-click';
import TruncatedTextWithAutoTooltip from '../../../../../shared/components/truncated-text-with-auto-tooltip';
import { Placement } from '../../../../../shared/design-system/components/overlay';

type AvatarCacheEntry =
  | { status: 'loading'; promise: Promise<string> }
  | { status: 'loaded'; objectUrl: string }
  | { status: 'error' };

const avatarLoadCache = new Map<string, AvatarCacheEntry>();

const fetchAvatarObjectUrl = (url: string): Promise<string> => {
  const cachedEntry = avatarLoadCache.get(url);

  if (cachedEntry) {
    if (cachedEntry.status === 'loaded') {
      return Promise.resolve(cachedEntry.objectUrl);
    }
    if (cachedEntry.status === 'loading') {
      return cachedEntry.promise;
    }
    // If previous attempt failed, fall through to retry.
  }

  const promise = fetch(url)
    .then((response) => {
      if (!response.ok) {
        throw new Error(`Failed to load avatar for ${url}`);
      }
      return response.blob();
    })
    .then((blob) => {
      const objectUrl = URL.createObjectURL(blob);
      avatarLoadCache.set(url, { status: 'loaded', objectUrl });
      return objectUrl;
    })
    .catch((error) => {
      avatarLoadCache.set(url, { status: 'error' });
      throw error;
    });

  avatarLoadCache.set(url, { status: 'loading', promise });
  return promise;
};

interface NameCellProps {
  fullName?: string | null;
  pictureUrl?: string | null;
  isRevealed?: boolean;
  isProspectCreated?: boolean;
  contactId?: number | null;
  onLeadClick?: (contactId?: number | null) => void;
  onProfileClick?: (rect: DOMRect) => void;
}

const NameCell: React.FC<NameCellProps> = ({
  fullName,
  pictureUrl: pictureUrlProp,
  isRevealed,
  isProspectCreated,
  contactId,
  onLeadClick,
  onProfileClick,
}) => {
  const INVALID_PICTURE_URL =
    'https://static.licdn.com/aero-v1/sc/h/9c8pery4andzj6ohjkjp54ma2';

  // Memoize expensive string operations
  const { displayName, firstName, lastName } = useMemo(() => {
    const name = fullName || '-';
    if (name && name !== '-') {
      const [first, ...rest] = name.split(' ');
      return {
        displayName: name,
        firstName: first || '',
        lastName: rest.join(' '),
      };
    }
    return {
      displayName: '-',
      firstName: '',
      lastName: '',
    };
  }, [fullName]);

  const isClickable = useMemo(
    () => Boolean(onLeadClick) && isRevealed && isProspectCreated,
    [onLeadClick, isRevealed, isProspectCreated],
  );

  const pictureUrl =
    pictureUrlProp && pictureUrlProp !== INVALID_PICTURE_URL
      ? pictureUrlProp
      : null;
  const cachedEntry = pictureUrl ? avatarLoadCache.get(pictureUrl) : undefined;

  const [resolvedSrc, setResolvedSrc] = useState<string | null>(() =>
    cachedEntry?.status === 'loaded' ? cachedEntry.objectUrl : null,
  );
  const [imageError, setImageError] = useState(
    () => cachedEntry?.status === 'error',
  );
  const [imageLoading, setImageLoading] = useState(
    () =>
      Boolean(pictureUrl) && (!cachedEntry || cachedEntry.status === 'loading'),
  );

  useEffect(() => {
    let isActive = true;

    if (!pictureUrl) {
      setResolvedSrc(null);
      setImageLoading(false);
      setImageError(false);
      return () => {
        isActive = false;
      };
    }

    const cacheEntry = avatarLoadCache.get(pictureUrl);
    if (cacheEntry?.status === 'loaded') {
      setResolvedSrc(cacheEntry.objectUrl);
      setImageLoading(false);
      setImageError(false);
      return () => {
        isActive = false;
      };
    }

    if (cacheEntry?.status === 'error') {
      setResolvedSrc(null);
      setImageLoading(false);
      setImageError(true);
      return () => {
        isActive = false;
      };
    }

    setResolvedSrc(null);
    setImageLoading(true);
    setImageError(false);

    const loadPromise =
      cacheEntry?.status === 'loading'
        ? cacheEntry.promise
        : fetchAvatarObjectUrl(pictureUrl);

    loadPromise
      .then((objectUrl) => {
        if (!isActive) {
          return;
        }
        setResolvedSrc(objectUrl);
        setImageLoading(false);
        setImageError(false);
      })
      .catch(() => {
        if (!isActive) {
          return;
        }
        setResolvedSrc(null);
        setImageError(true);
        setImageLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [pictureUrl]);

  const hasPicture = Boolean(pictureUrl);
  const shouldShowAvatar = !hasPicture || imageError;
  const shouldShowSkeleton = hasPicture && imageLoading && !imageError;

  // Memoize avatar content
  const avatarContent = useMemo(() => {
    if (shouldShowAvatar) {
      return (
        <Avatar
          firstName={firstName || displayName.charAt(0)}
          lastName={lastName}
          className="v2-lead-avatar-placeholder"
        />
      );
    }

    return (
      <>
        {shouldShowSkeleton && (
          <SkeletonLoading className="d-flex" width={24} height={24} circle />
        )}
        {resolvedSrc && (
          <img
            src={resolvedSrc}
            alt=""
            className="v2-lead-avatar-img"
            loading="lazy"
            style={{ display: shouldShowSkeleton ? 'none' : 'block' }}
          />
        )}
      </>
    );
  }, [
    shouldShowAvatar,
    shouldShowSkeleton,
    resolvedSrc,
    firstName,
    lastName,
    displayName,
  ]);

  // Memoize click handler to keep props stable between renders
  const clickHandler = useMemo(() => {
    if (!isClickable || !onLeadClick) {
      return {};
    }

    return accessibleOnClick(() => onLeadClick(contactId));
  }, [isClickable, onLeadClick, contactId]);

  const handleProfileClick = useMemo(() => {
    if (!onProfileClick) return undefined;
    return (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      const button = e.currentTarget.querySelector(
        'button',
      ) as HTMLElement | null;
      const indicator = e.currentTarget.querySelector(
        '.v2-lead-modal-open-indicator',
      ) as HTMLElement | null;
      const anchorElement = button || e.currentTarget;
      const anchorRect = anchorElement.getBoundingClientRect();
      const indicatorRect = indicator?.getBoundingClientRect();
      const anchorRight =
        indicatorRect && indicatorRect.width > 0
          ? indicatorRect.right
          : anchorRect.right;
      const rect = new DOMRect(
        anchorRect.left,
        anchorRect.top,
        anchorRight - anchorRect.left,
        anchorRect.height,
      );
      onProfileClick(rect);
    };
  }, [onProfileClick]);

  return (
    <div className="v2-lead-name-cell" {...clickHandler}>
      <div
        className="v2-lead-avatar"
        {...(onProfileClick
          ? accessibleOnClick((e) => {
              e.stopPropagation();
              onProfileClick(
                (e.currentTarget as HTMLElement).getBoundingClientRect(),
              );
            })
          : {})}
      >
        {avatarContent}
      </div>
      {onProfileClick ? (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
        <div className="v2-lead-name-text" onClick={handleProfileClick}>
          <button
            type="button"
            className="v2-lead-name-text--clickable d-flex align-items-center gap-4"
          >
            <TruncatedTextWithAutoTooltip
              text={displayName}
              placement={Placement.Auto}
            />
            <span className="v2-lead-modal-open-indicator" aria-hidden="true">
              <ArrowsDiagonal width={14} height={14} />
            </span>
          </button>
        </div>
      ) : (
        <div className="v2-lead-name-text">
          <TruncatedTextWithAutoTooltip
            text={displayName}
            placement={Placement.Auto}
          />
        </div>
      )}
    </div>
  );
};

// Memoize NameCell to prevent unnecessary re-renders
export default React.memo(NameCell);
